import { useCallback, useState } from "react";
import {
  ExerciseEntry,
  ExerciseProgression,
  Programmazione,
  Split,
} from "../types/Programmazione";
import type { PrecompiledSet, PrecompiledWeek } from "../types/Entities";
import { Programmazioni } from "../api/Programmazioni";
import { useToasts } from "../contexts/toasts";
import { appendMediaFiles } from "../utils/programmazioneMediaAppender";
import { v4 } from "uuid";

type Args = {
  programmazione: Programmazione | undefined;
  setProgrammazione: (p: Programmazione) => void;
  id: string | undefined;
};

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

async function saveProgrammazione(
  id: string,
  updated: Programmazione,
): Promise<Programmazione | undefined> {
  const formData = new FormData();
  // PROG-DETAIL-NEW/6 — strip per-set image/video File handles into the
  // FormData under the canonical bracket-path keys before stringifying.
  // Without this, the JSON.stringify below silently drops File instances
  // (they serialize to `{}`) and the BE never receives the upload.
  // Matches the existing flow in pages/Programmazioni/EditProgrammazione.tsx.
  appendMediaFiles(updated as unknown as Record<string, unknown>, formData);
  formData.append("programmazione", JSON.stringify(updated));
  const { data } = await Programmazioni.updateProgrammazione(id, formData);
  // Return the SERVER copy: it carries the persisted media keys turned into
  // signed URLs (imageUrl/videoUrl + role-media signedUrl). The local `updated`
  // had its File handles stripped by appendMediaFiles and its *Url left null,
  // so reusing it would make a freshly-uploaded media vanish from the UI.
  return data?.programmazione;
}

export function useProgrammazioneMutations({ programmazione, setProgrammazione, id }: Args) {
  const { showToast } = useToasts();
  const [saving, setSaving] = useState(false);

  const mutate = useCallback(
    async (mutationFn: (clone: Programmazione) => void, successMsg?: string) => {
      if (!programmazione || !id) return;
      setSaving(true);
      try {
        const updated = deepClone(programmazione);
        mutationFn(updated);
        updated.nSplits = updated.splits?.length || 0;
        const saved = await saveProgrammazione(id, updated);
        // Prefer the server copy (signed media URLs); fall back to the local
        // optimistic object only if the response is unexpectedly empty.
        setProgrammazione(saved ?? updated);
        if (successMsg) showToast("mutation-success", successMsg, "", "success");
      } catch {
        showToast("mutation-error", "Errore nel salvataggio", "", "danger");
      } finally {
        setSaving(false);
      }
    },
    [programmazione, id, setProgrammazione, showToast]
  );

  // ─── Split ──────────────────────────────────────────────────────────────────

  const addSplit = useCallback(
    () =>
      mutate((p) => {
        p.splits.push({ dayName: `Split ${p.splits.length + 1}`, exercises: [] });
      }, "Split aggiunto"),
    [mutate]
  );

  const deleteSplit = useCallback(
    (splitIndex: number) =>
      mutate((p) => {
        p.splits.splice(splitIndex, 1);
      }, "Split eliminato"),
    [mutate]
  );

  const pasteSplit = useCallback(
    (copiedSplit: Split) =>
      mutate((p) => {
        const cloned = deepClone(copiedSplit);
        cloned.dayName = `${cloned.dayName} (copia)`;
        p.splits.push(cloned);
      }, "Split incollato"),
    [mutate]
  );

  const reorderSplits = useCallback(
    (sourceIndex: number, destIndex: number) =>
      mutate((p) => {
        const [moved] = p.splits.splice(sourceIndex, 1);
        p.splits.splice(destIndex, 0, moved);
      }, "Ordine splits aggiornato"),
    [mutate]
  );

  // ─── Exercise ───────────────────────────────────────────────────────────────

  const addExercise = useCallback(
    (splitIndex: number) =>
      mutate((p) => {
        p.splits[splitIndex].exercises.push({
          id: v4(),
          customName: "",
          type: "custom",
        });
      }, "Esercizio aggiunto"),
    [mutate]
  );

  const addExerciseFull = useCallback(
    (splitIndex: number, exercise: ExerciseEntry) =>
      mutate((p) => {
        const cloned = deepClone(exercise);
        cloned.id = v4();
        p.splits[splitIndex].exercises.push(cloned);
      }, "Esercizio aggiunto"),
    [mutate]
  );

  const deleteExercise = useCallback(
    (splitIndex: number, exerciseIndex: number) =>
      mutate((p) => {
        p.splits[splitIndex].exercises.splice(exerciseIndex, 1);
      }, "Esercizio eliminato"),
    [mutate]
  );

  const pasteExercise = useCallback(
    (splitIndex: number, copiedExercise: ExerciseEntry) =>
      mutate((p) => {
        const cloned = deepClone(copiedExercise);
        cloned.id = v4();
        p.splits[splitIndex].exercises.push(cloned);
      }, "Esercizio incollato"),
    [mutate]
  );

  const reorderExercises = useCallback(
    (splitIndex: number, sourceIndex: number, destIndex: number) =>
      mutate((p) => {
        const exercises = p.splits[splitIndex].exercises;
        const [moved] = exercises.splice(sourceIndex, 1);
        exercises.splice(destIndex, 0, moved);
      }, "Ordine esercizi aggiornato"),
    [mutate]
  );

  // ─── Exercise field update ─────────────────────────────────────────────────

  const updateExerciseField = useCallback(
    (splitIndex: number, exerciseIndex: number, fieldKey: string, newValue: any) =>
      mutate((p) => {
        (p.splits[splitIndex].exercises[exerciseIndex] as any)[fieldKey] = newValue;
      }, "Campo aggiornato"),
    [mutate]
  );

  // Batched multi-field update: apply every field in `patch` inside a SINGLE
  // mutate() (one deepClone + one save PUT). Callers editing several fields at
  // once (e.g. the identity modal: type + muscleGroupId + exerciseId +
  // customName) MUST use this instead of chaining updateExerciseField — each
  // updateExerciseField call clones the SAME stale `programmazione` closure and
  // fires its own PUT, so the concurrent saves race and the last one to land on
  // the server wins, silently dropping the other fields' changes.
  const updateExerciseFields = useCallback(
    (
      splitIndex: number,
      exerciseIndex: number,
      patch: Partial<ExerciseEntry>,
    ) =>
      mutate((p) => {
        Object.assign(p.splits[splitIndex].exercises[exerciseIndex], patch);
      }, "Esercizio aggiornato"),
    [mutate],
  );

  // ─── Split field update ────────────────────────────────────────────────────

  const updateSplitField = useCallback(
    (splitIndex: number, fieldKey: string, newValue: any) =>
      mutate((p) => {
        (p.splits[splitIndex] as any)[fieldKey] = newValue;
      }, "Campo aggiornato"),
    [mutate]
  );

  // ─── Progression / Week / Set updates (PROG-DETAIL-NEW/2 #8456d4e2) ───────
  //
  // Replacement-style mutations consumed by the Detail page InlineEditModal:
  // each commit is explicit on user OK, so no debounce / coalescing here.

  const updateExerciseProgression = useCallback(
    (
      splitIndex: number,
      exerciseIndex: number,
      nextProgression: ExerciseProgression,
    ) =>
      mutate((p) => {
        p.splits[splitIndex].exercises[exerciseIndex].progression =
          nextProgression;
      }, "Progressione aggiornata"),
    [mutate],
  );

  const updateProgressionWeek = useCallback(
    (
      splitIndex: number,
      exerciseIndex: number,
      weekIndex: number,
      patch: Partial<PrecompiledWeek>,
    ) =>
      mutate((p) => {
        const w =
          p.splits[splitIndex].exercises[exerciseIndex].progression?.weeks?.[
            weekIndex
          ];
        if (w) Object.assign(w, patch);
      }, "Settimana aggiornata"),
    [mutate],
  );

  const addProgressionSet = useCallback(
    (
      splitIndex: number,
      exerciseIndex: number,
      weekIndex: number,
      set: PrecompiledSet,
    ) =>
      mutate((p) => {
        const w =
          p.splits[splitIndex].exercises[exerciseIndex].progression?.weeks?.[
            weekIndex
          ];
        if (!w) return;
        w.sets = [...(w.sets ?? []), deepClone(set)];
      }, "Set aggiunto"),
    [mutate],
  );

  const updateProgressionSet = useCallback(
    (
      splitIndex: number,
      exerciseIndex: number,
      weekIndex: number,
      setIndex: number,
      patch: Partial<PrecompiledSet>,
      // Optional override so callers (e.g. the media editor) can surface a
      // more specific success toast than the default "Set aggiornato".
      successMsg = "Set aggiornato",
    ) =>
      mutate((p) => {
        const s =
          p.splits[splitIndex].exercises[exerciseIndex].progression?.weeks?.[
            weekIndex
          ]?.sets?.[setIndex];
        if (s) Object.assign(s, patch);
      }, successMsg),
    [mutate],
  );

  // ─── User assignment ──────────────────────────────────────────────────────

  const updateUserId = useCallback(
    (userId: number | null) =>
      mutate((p) => {
        (p as any).userId = userId;
      }, "Utente assegnato"),
    [mutate]
  );

  return {
    saving,
    // Split
    addSplit,
    deleteSplit,
    pasteSplit,
    reorderSplits,
    // Exercise
    addExercise,
    addExerciseFull,
    deleteExercise,
    pasteExercise,
    reorderExercises,
    // Fields
    updateExerciseField,
    updateExerciseFields,
    updateSplitField,
    // Progression updates (DETAIL-NEW-2)
    updateExerciseProgression,
    updateProgressionWeek,
    addProgressionSet,
    updateProgressionSet,
    // User
    updateUserId,
  };
}
